// GET /api/selfcare/sessions — HAQIQIY backend kontrakti
// (`xsib-core/selfcare/internal/SessionsController.java`), ikkita ALOHIDA
// ro'yxat qaytaradi — bitta emas:
//   { active: ActiveSession[], recent: SessionHistory[], hasMore: boolean }
//
// Maydonlar `session.api.ActiveSession` / `session.api.SessionHistory`
// yozuvlaridan (`xsib-core/session/src/main/java/uz/xsib/session/api/`)
// ANIQ ko'chirilgan — taxmin qilinmagan:
//   · inputOctets  = abonent YUBORGAN (upload)
//   · outputOctets = abonent OLGAN (download)
// Bu yo'nalish NAS nuqtai nazaridan qo'yilgan va chalkash — shuning uchun
// bu yerda darhol `uploadBytes`/`downloadBytes` ga o'giriladi, boshqa hech
// qayerda xom nom ishlatilmasin.

import { z } from 'zod'
import { api } from '@/shared/api/client'

/**
 * ⚠️ `username` BU YERDA YO'Q — VA AYNAN SHU NUQSON SAHIFANI O'LDIRGAN EDI.
 *
 * Sxemada `username: z.string()` MAJBURIY deb yozilgan edi, backend esa
 * uni umuman yubormaydi: kabinet javobida abonentning login'i ortiqcha
 * (u o'z login'ini biladi) va `SessionsResponse` uni tashlab yuboradi.
 *
 * Natijasi eng yomon shakl edi: `zod` butun javobni rad etardi →
 * `fetchSessions` istisno tashlardi → React Query uch marta qayta
 * urinardi → shu vaqt davomida so'rov `isPending` holatida qolardi va
 * sahifada MANGU SKELET turardi. Xato xabari ham chiqmasdi, chunki
 * ekran hech qachon `isError` holatiga yetib bormasdi — abonent
 * "sessiyalar ko'rinmayapti" degan holatni ko'rardi va sabab
 * ko'rsatilmasdi.
 *
 * ⚠️ QOLGAN MAYDONLAR HAM `.nullish()`: Jackson `non_null` bilan
 * sozlangan, ya'ni null maydon JSON'da UMUMAN BO'LMAYDI. Majburiy deb
 * belgilangan har qanday maydon shu sababdan sahifani yiqitishi mumkin.
 */
const activeSessionSchema = z.object({
  radacctId: z.number(),
  framedIp: z.string().nullish(),
  callingStationId: z.string().nullish(),
  nasIp: z.string().nullish(),
  startTime: z.string(),
  lastUpdate: z.string().nullish(),
  sessionTime: z.number(),
  inputOctets: z.number(),
  outputOctets: z.number(),
  acctSessionId: z.string().nullish(),
  interimInterval: z.number().nullish(),
})

/** ⚠️ `username` bu yerda ham YO'Q — sabab yuqorida. */
const sessionHistorySchema = z.object({
  radacctId: z.number(),
  framedIp: z.string().nullish(),
  callingStationId: z.string().nullish(),
  nasIp: z.string().nullish(),
  startTime: z.string(),
  stopTime: z.string().nullish(),
  sessionTime: z.number(),
  inputOctets: z.number(),
  outputOctets: z.number(),
  acctSessionId: z.string().nullish(),
  terminateCause: z.string().nullish(),
})

const sessionsResponseSchema = z.object({
  active: z.array(activeSessionSchema),
  recent: z.array(sessionHistorySchema),
  hasMore: z.boolean(),
})

export interface ActiveSubscriberSession {
  radacctId: number
  ipAddress: string | null
  device: string | null
  nasIp: string | null
  startedAt: string
  lastUpdate: string | null
  durationSeconds: number
  uploadBytes: number
  downloadBytes: number
}

export interface SubscriberSessionHistory {
  radacctId: number
  ipAddress: string | null
  device: string | null
  nasIp: string | null
  startedAt: string
  /** `null` — sessiya hali ochiq (tugamagan) */
  endedAt: string | null
  durationSeconds: number
  uploadBytes: number
  downloadBytes: number
  terminateCause: string | null
}

export interface SessionsResult {
  active: ActiveSubscriberSession[]
  recent: SubscriberSessionHistory[]
  hasMore: boolean
}

export async function fetchSessions(): Promise<SessionsResult> {
  const response = await api.get<unknown>('/api/selfcare/sessions')
  const parsed = sessionsResponseSchema.parse(response.data)

  return {
    active: parsed.active.map((s) => ({
      radacctId: s.radacctId,
      ipAddress: s.framedIp ?? null,
      device: s.callingStationId ?? null,
      nasIp: s.nasIp ?? null,
      startedAt: s.startTime,
      lastUpdate: s.lastUpdate ?? null,
      durationSeconds: s.sessionTime,
      uploadBytes: s.inputOctets,
      downloadBytes: s.outputOctets,
    })),
    recent: parsed.recent.map((s) => ({
      radacctId: s.radacctId,
      ipAddress: s.framedIp ?? null,
      device: s.callingStationId ?? null,
      nasIp: s.nasIp ?? null,
      startedAt: s.startTime,
      endedAt: s.stopTime ?? null,
      durationSeconds: s.sessionTime,
      uploadBytes: s.inputOctets,
      downloadBytes: s.outputOctets,
      terminateCause: s.terminateCause ?? null,
    })),
    hasMore: parsed.hasMore,
  }
}
